import { FunctionComponent, useState } from "react";
import classNames from "classnames";
import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Stack,
  TooltipCore,
  TooltipTrigger,
  TooltipContent,
} from "@altimateai/lego";
import { IconMessageChatbot } from "@tabler/icons-react";
import { openAltimateCodeChat, telemetryEvents } from "../api";

export interface ChatStarterQuestion {
  id: string;
  label: string;
}

// Contextual starter questions surfaced when the user opens the chat trigger.
// `label` is sent verbatim to Altimate Code Chat; `id` is the telemetry key.
export const MODEL_CHAT_QUESTIONS: ChatStarterQuestion[] = [
  { id: "model_summary", label: "Summarize what this model does" },
  {
    id: "model_upstream",
    label: "What are the upstream dependencies of this model?",
  },
  {
    id: "model_downstream",
    label: "What downstream models depend on this one?",
  },
  {
    id: "model_quality",
    label: "Are there data quality risks or missing tests here?",
  },
];

export const COLUMN_CHAT_QUESTIONS: ChatStarterQuestion[] = [
  { id: "column_calculation", label: "How is this column calculated?" },
  { id: "column_origin", label: "Where does this column originate upstream?" },
  {
    id: "column_downstream_impact",
    label: "What downstream models are affected if this column changes?",
  },
  {
    id: "column_semantics",
    label: "What data type and semantics does this column have?",
  },
];

interface Props {
  level: "model" | "column";
  /** unique_id / table_rk of the model the trigger belongs to. */
  table: string;
  /** Column name — required for column-level triggers. */
  column?: string;
  /** Tooltip + accessible label describing the action. */
  tooltip: string;
  /** Extra classes for the trigger button (used for hover/visibility). */
  className?: string;
}

const ChatStarterButton: FunctionComponent<Props> = ({
  level,
  table,
  column,
  tooltip,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const questions = level === "model" ? MODEL_CHAT_QUESTIONS : COLUMN_CHAT_QUESTIONS;

  const launch = (question: ChatStarterQuestion | null) => {
    telemetryEvents({
      id: level === "model" ? "lineageChatAboutModel" : "lineageChatAboutColumn",
      params: { table, column },
    });
    if (question) {
      telemetryEvents({
        id: "lineageChatStarterQuestionSelected",
        params: { level, question_id: question.id },
      });
    }
    openAltimateCodeChat({
      table,
      column,
      level,
      question: question?.label ?? "",
    });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* TooltipContent + PopoverContent both get al-z-[1000] so they render
          above the lineage sidebar (z-index ~998). See ColumnCard for context. */}
      <TooltipCore>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              title={tooltip}
              aria-label={tooltip}
              className={classNames("al-w-7 al-h-7", className)}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
              }}
            >
              <IconMessageChatbot className="al-w-4 al-h-4" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent className="al-z-[1000]">{tooltip}</TooltipContent>
      </TooltipCore>
      <PopoverContent
        // !al-p-1 overrides the PopoverContent card default (al-p-4); this
        // popover is a menu of questions, so it uses the dropdown-menu padding
        // (al-p-1 content + al-px-2 al-py-1.5 items) instead of the 16px card.
        className="al-z-[1000] al-tw-scope al-w-72 !al-p-1"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <Stack direction="column" gap="xs">
          <div className="al-text-xs al-text-gray-500 al-px-2 al-py-1.5">Ask Altimate Code</div>
          {questions.map(q => (
            <Button
              key={q.id}
              size="xs"
              variant="ghost"
              // `size="xs"` gives the section-standard al-text-xs and an auto
              // height. !-important overrides: the Button base sets
              // justify-center and whitespace-nowrap, and Tailwind's stylesheet
              // order would let those win — force left-aligned wrapping text so
              // long questions fit inside the popover.
              className="!al-justify-start al-text-left al-w-full !al-whitespace-normal"
              onClick={() => launch(q)}
            >
              {q.label}
            </Button>
          ))}
          <Button
            size="xs"
            variant="link"
            className="al-justify-start"
            onClick={() => launch(null)}
          >
            Ask something else…
          </Button>
        </Stack>
      </PopoverContent>
    </Popover>
  );
};

export default ChatStarterButton;
